import { makeAutoObservable } from 'mobx';
import { Result } from 'neverthrow';

import { IDataStore } from '../../../features/data-storage/features';
import { INotifications } from '../../../features/notifications/core/notifications';
import { Fail } from '../../data/fail';
import { HttpRequest, IHttpClient } from '../http-client';
import { ObservableRequest } from '../observable-request';

export interface IHttpResource<Params> {
  get isLoading(): boolean;
  get hasError(): boolean;
  get error(): string;

  send(params: Params): void;
  reset(): void;
}

/*
 1. Если нужен сайд-эффект или какая-то реакция, используй параметр onFinish(enttity: Entity): void
 Например, можно сделать редирект с помощью роутера на созаднную сущность.

 2. Для сохранение данных во внешнее хранилище используй параметр dataStore;
 В него необходимо передать объект, которы реализует интерфейс IDataStorage<Entity>
*/

type FinishCallback<Params> = (result: Params) => void;
type ErrorCallback = (error: Fail) => void;
type OptFnArg<Param, Result> = [Param] extends [void]
  ? () => Result
  : (arg: Param) => Result;

type HttpResourceResourceParams<Params, Done> = {
  apiClient: IHttpClient;
  httpRequest: OptFnArg<Params, HttpRequest>;
  errorNotify?: INotifications;
  onFinish?: FinishCallback<Done>;
  onError?: ErrorCallback;
  dataStore?: IDataStore<Done>;
};

export class HttpResource<Params, Done> implements IHttpResource<Params> {
  private request: ObservableRequest<Params, Done>;
  private finishCallback?: FinishCallback<Done>;
  private errorNotify?: INotifications;
  private errorCallback?: ErrorCallback;
  private dataStore?: IDataStore<Done>;
  private _error = '';

  public get isLoading() {
    return this.request.isLoading;
  }

  public get isWaitingForLoad() {
    return this.request.isLoading || this.request.isIdle;
  }

  public get hasError(): boolean {
    return !!this._error;
  }

  public get error(): string {
    return this._error;
  }

  constructor({
    apiClient,
    httpRequest,
    errorNotify,
    onFinish,
    onError,
    dataStore,
  }: HttpResourceResourceParams<Params, Done>) {
    const request = (params: Params) =>
      apiClient.fetch<Done>(httpRequest(params));

    // ts-expect-error
    this.request = new ObservableRequest<Params, Done>(
      request as OptFnArg<Params, Promise<Result<Done, Fail>>>,
      {
        done: (result) => this.handleFinish(result),
        fail: (error) => this.handleError(error),
      }
    );

    this.finishCallback = onFinish;
    this.errorNotify = errorNotify;
    this.errorCallback = onError;
    this.dataStore = dataStore;

    makeAutoObservable(this);
  }

  private handleFinish(result: Done) {
    this.dataStore && this.dataStore.setData(result);
    this.finishCallback && this.finishCallback(result);
  }

  private handleError(error: Fail) {
    this._error = error.message;
    this.errorCallback && this.errorCallback(error);

    this.errorNotify &&
      this.errorNotify.addMessage({ type: 'error', message: error.message });
  }

  public send(params: Params) {
    this.request.fetch(params);
  }

  public reset(): void {
    this.request.reset();
    this._error = '';
  }
}
